/**
 * Exchange Routers screen — ported from
 * dashboard/src/views/admin/ExchangeRouters/index.jsx.
 *
 * Live operational view of NUREV's three crypto-payout routes:
 *   - ChangeNOW
 *   - Exolix
 *   - StealthEX
 *
 * Goal: spot stuck transactions, compare effective spreads, and
 * intervene when something hangs. Polls /admin-services/exchange-routers
 * every 30s. Falls back to "Not configured" empty state when the
 * endpoint isn't wired or no API keys are present (404 → data null).
 *
 * Port notes:
 *   - AnimatedCounter / Sparkline not available in this repo; replaced
 *     with plain text and a lightweight inline SVG bar chart.
 *   - StatusPill replaced with Badge from atoms.tsx + status map.
 *   - axiosClient (auto-unwrap) → client + unwrap() in api/client.ts.
 *   - react-feather → lucide-react.
 *   - All other logic, tabs, filters, and field names are verbatim.
 */
import { useMemo, useState } from 'react';
import {
  RefreshCw,
  ExternalLink,
  AlertTriangle,
  ArrowRight,
  TrendingDown,
  Zap
} from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { ROUTERS, ExchangeTransaction } from '@/api/exchangeRouters';
import { useExchangeRouters } from '@/hooks/useExchangeRouters';
import { Card, SectionLabel, Skeleton, StatCard } from '@/components/ui/atoms';
import { timeAgo, fmtNum } from '@/lib/format';

// ─── Status → badge style mapping ────────────────────────────────────────

const STATUS_BADGE: Record<string, string> = {
  // healthy
  completed:    'bg-emerald-500/20 text-emerald-300 ring-1 ring-emerald-500/30',
  succeeded:    'bg-emerald-500/20 text-emerald-300 ring-1 ring-emerald-500/30',
  // warn / in-flight
  confirming:   'bg-amber-500/20 text-amber-300 ring-1 ring-amber-500/30',
  transferring: 'bg-amber-500/20 text-amber-300 ring-1 ring-amber-500/30',
  // pending
  initiated:    'bg-sky-500/20 text-sky-300 ring-1 ring-sky-500/30',
  converting:   'bg-sky-500/20 text-sky-300 ring-1 ring-sky-500/30',
  // error
  failed:       'bg-red-500/20 text-red-300 ring-1 ring-red-500/30',
  stuck:        'bg-red-500/20 text-red-300 ring-1 ring-red-500/30',
};

function StatusBadge({ status }: { status: string }) {
  const key = String(status).toLowerCase();
  const cls = STATUS_BADGE[key] || 'bg-white/10 text-nurev-muted ring-1 ring-white/10';
  return (
    <span className={`inline-block rounded px-2 py-0.5 text-[10px] font-medium ${cls}`}>
      {status}
    </span>
  );
}

// ─── Stuck-transaction predicate (> 60 min in an in-flight status) ────────

const IN_FLIGHT = new Set(['initiated', 'transferring', 'confirming', 'converting']);

function isStuck(t: ExchangeTransaction): boolean {
  const ageMin = (Date.now() - new Date(t.createdAt).getTime()) / 60_000;
  return IN_FLIGHT.has(String(t.status).toLowerCase()) && ageMin > 60;
}

// ─── Inline sparkline — no external dep, pure SVG ─────────────────────────

function Sparkline({ data }: { data: number[] }) {
  if (!data || data.length < 2) return null;
  const max = Math.max(...data, 1);
  const w = 200;
  const h = 28;
  const step = w / (data.length - 1);
  const pts = data
    .map((v, i) => `${i * step},${h - (v / max) * (h - 2)}`)
    .join(' ');
  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="w-full" preserveAspectRatio="none">
      <polyline
        points={pts}
        fill="none"
        stroke="rgba(167,139,250,0.6)"
        strokeWidth="1.5"
        strokeLinejoin="round"
        strokeLinecap="round"
      />
    </svg>
  );
}

// ─── Per-router status dot ────────────────────────────────────────────────

function RouterStatusDot({
  configured,
  tx24h,
  fail24h
}: {
  configured: boolean;
  tx24h: number;
  fail24h: number;
}) {
  if (!configured) {
    return (
      <span className="inline-block rounded px-2 py-0.5 text-[10px] font-medium bg-white/10 text-nurev-muted ring-1 ring-white/10">
        Not connected
      </span>
    );
  }
  const warn = fail24h > tx24h * 0.05;
  return (
    <span
      className={`inline-flex items-center gap-1 rounded px-2 py-0.5 text-[10px] font-medium ring-1 ${
        warn
          ? 'bg-amber-500/20 text-amber-300 ring-amber-500/30'
          : 'bg-emerald-500/20 text-emerald-300 ring-emerald-500/30'
      }`}
    >
      <span
        className={`inline-block h-1.5 w-1.5 rounded-full ${
          tx24h > 0
            ? warn
              ? 'animate-pulse bg-amber-400'
              : 'animate-pulse bg-emerald-400'
            : warn
              ? 'bg-amber-400'
              : 'bg-emerald-400'
        }`}
      />
      {warn ? 'Degraded' : 'Healthy'}
    </span>
  );
}

// ─── Filter types ─────────────────────────────────────────────────────────

type Filter = 'all' | 'stuck' | 'changenow' | 'exolix' | 'stealthex';

const FILTER_TABS: Array<{ key: Filter; label: string }> = [
  { key: 'all',       label: 'All' },
  { key: 'stuck',     label: 'Stuck' },
  ...ROUTERS.map(r => ({ key: r.id as Filter, label: r.label })),
];

// ─── Main screen ──────────────────────────────────────────────────────────

export function ExchangeRoutersScreen() {
  const { data, isLoading, isError, error, dataUpdatedAt } = useExchangeRouters();
  const queryClient = useQueryClient();
  const [filter, setFilter] = useState<Filter>('all');

  const summary    = data?.summary    || {};
  const routerLive = data?.routers    || [];
  const transactions = data?.transactions || [];

  const stuckCount = useMemo(
    () => transactions.filter(isStuck).length,
    [transactions]
  );

  const filteredTx = useMemo(() => {
    if (filter === 'all')   return transactions;
    if (filter === 'stuck') return transactions.filter(isStuck);
    return transactions.filter(t => t.router === filter);
  }, [filter, transactions]);

  const lastUpdated = dataUpdatedAt
    ? new Date(dataUpdatedAt).toLocaleTimeString()
    : null;

  function refresh() {
    void queryClient.invalidateQueries({ queryKey: ['adminExchangeRouters'] });
  }

  // Volume24h formatted as USD
  const vol24hStr =
    summary.volume24hUsd != null
      ? `$${Number(summary.volume24hUsd).toFixed(2)}`
      : '$0.00';

  const avgSpreadStr =
    summary.avgSpread != null
      ? `${(summary.avgSpread * 100).toFixed(2)}%`
      : '—';

  return (
    <div className="mx-auto max-w-7xl space-y-5">

      {/* ── Header ── */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-nurev-text lg:text-2xl">
            Exchange Routers
          </h1>
          <p className="mt-1 text-xs text-nurev-muted">
            Live operational view of every crypto payout route. Polls every 30 seconds.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {lastUpdated && (
            <span className="text-[10px] text-nurev-muted">
              updated {lastUpdated}
            </span>
          )}
          <button
            onClick={refresh}
            disabled={isLoading}
            className="inline-flex cursor-pointer items-center gap-1.5 rounded-lg border border-nurev-border bg-white/[0.04] px-3 py-1.5 text-xs font-bold text-nurev-text transition-colors hover:bg-white/[0.07] disabled:opacity-50"
          >
            <RefreshCw
              size={12}
              className={isLoading ? 'animate-spin' : ''}
            />
            Refresh
          </button>
        </div>
      </div>

      {/* ── Error banner (non-404 failures) ── */}
      {isError && (
        <div className="rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300">
          {(error as Error)?.message || 'Failed to load exchange router data.'}
        </div>
      )}

      {/* ── Aggregate KPI cards ── */}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatCard
          label="Volume (24h)"
          value={isLoading ? '…' : vol24hStr}
          sub={`${fmtNum(summary.tx24h ?? null)} transactions`}
          loading={isLoading}
        />
        <StatCard
          label="Average Spread"
          value={isLoading ? '…' : avgSpreadStr}
          sub="across all routers"
          loading={isLoading}
        />
        <StatCard
          label="Stuck Transactions"
          value={isLoading ? '…' : fmtNum(stuckCount)}
          sub="pending > 60 min"
          color={stuckCount > 0 ? 'text-amber-300' : undefined}
          loading={isLoading}
        />
        <StatCard
          label="Cheapest Route"
          value={isLoading ? '…' : (summary.cheapestRouter || '—')}
          sub="based on last 100 swaps"
          color="text-emerald-300"
          loading={isLoading}
        />
      </div>

      {/* ── Per-router cards ── */}
      <div className="grid grid-cols-1 gap-3 lg:grid-cols-3">
        {ROUTERS.map(r => {
          const live      = routerLive.find(x => x.id === r.id);
          const configured = !!live?.configured;
          const tx24h     = live?.tx24h     ?? 0;
          const fail24h   = live?.failed24h ?? 0;
          const spread    = live?.avgSpread != null ? live.avgSpread * 100 : null;
          const dailyTx   = Array.isArray(live?.dailyTx) ? live!.dailyTx : [];

          return (
            <Card key={r.id}>
              {/* Card header */}
              <div className="mb-3 flex items-center justify-between">
                <div>
                  <p className="text-sm font-bold text-nurev-text">{r.label}</p>
                  <p className="text-[10px] text-nurev-muted">
                    {r.url.replace('https://', '')}
                  </p>
                </div>
                {isLoading ? (
                  <Skeleton className="h-5 w-20" />
                ) : (
                  <RouterStatusDot
                    configured={configured}
                    tx24h={tx24h}
                    fail24h={fail24h}
                  />
                )}
              </div>

              {/* Metrics or unconfigured hint */}
              {isLoading ? (
                <Skeleton className="h-12 w-full" />
              ) : configured ? (
                <>
                  <div className="mb-3 grid grid-cols-3 gap-2">
                    <div>
                      <p className="text-[9px] font-bold uppercase text-nurev-muted">
                        Tx 24h
                      </p>
                      <p className="text-lg font-bold text-nurev-text">
                        {fmtNum(tx24h)}
                      </p>
                    </div>
                    <div>
                      <p className="text-[9px] font-bold uppercase text-nurev-muted">
                        Failed
                      </p>
                      <p
                        className={`text-lg font-bold ${
                          fail24h > 0 ? 'text-red-400' : 'text-nurev-text'
                        }`}
                      >
                        {fmtNum(fail24h)}
                      </p>
                    </div>
                    <div>
                      <p className="text-[9px] font-bold uppercase text-nurev-muted">
                        Spread
                      </p>
                      <p className="text-lg font-bold text-nurev-text">
                        {spread != null ? `${spread.toFixed(2)}%` : '—'}
                      </p>
                    </div>
                  </div>
                  {dailyTx.length > 1 && <Sparkline data={dailyTx} />}
                </>
              ) : (
                <p className="text-[11px] text-nurev-muted">
                  Add the API key in K8s secret to enable live router monitoring.
                </p>
              )}

              {/* Vendor link */}
              <div className="mt-3 flex items-center justify-end">
                <a
                  href={r.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-[11px] text-amber-400 hover:underline"
                >
                  Vendor dashboard
                  <ExternalLink size={10} />
                </a>
              </div>
            </Card>
          );
        })}
      </div>

      {/* ── Transaction ledger ── */}
      <div className="overflow-hidden rounded-2xl border border-nurev-border bg-nurev-surface">
        {/* Ledger header + filter tabs */}
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-nurev-border px-5 py-3">
          <div className="flex items-center gap-2">
            <Zap size={14} className="text-amber-400" />
            <h3 className="text-sm font-bold text-nurev-text">
              Transaction ledger
            </h3>
            {stuckCount > 0 && (
              <span className="inline-flex items-center gap-1 text-[10px] font-bold text-amber-300">
                <AlertTriangle size={11} />
                {stuckCount} stuck
              </span>
            )}
          </div>
          <div className="flex flex-wrap gap-1.5">
            {FILTER_TABS.map(f => (
              <button
                key={f.key}
                onClick={() => setFilter(f.key)}
                className={`cursor-pointer rounded-lg border-none px-2.5 py-1 text-[11px] font-bold transition-colors ${
                  f.key === filter
                    ? 'bg-amber-500 text-nurev-bg'
                    : 'bg-white/[0.04] text-nurev-muted hover:text-nurev-text'
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-nurev-border text-nurev-muted">
                <th className="px-5 py-2.5 text-left text-[10px] font-medium uppercase tracking-wider">
                  Time
                </th>
                <th className="px-5 py-2.5 text-left text-[10px] font-medium uppercase tracking-wider">
                  Router
                </th>
                <th className="px-5 py-2.5 text-left text-[10px] font-medium uppercase tracking-wider">
                  Status
                </th>
                <th className="px-5 py-2.5 text-left text-[10px] font-medium uppercase tracking-wider">
                  Pair
                </th>
                <th className="px-5 py-2.5 text-right text-[10px] font-medium uppercase tracking-wider">
                  Amount
                </th>
                <th className="px-5 py-2.5 text-right text-[10px] font-medium uppercase tracking-wider">
                  Rate
                </th>
                <th className="px-5 py-2.5 text-right text-[10px] font-medium uppercase tracking-wider">
                  Spread
                </th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={7} className="px-5 py-6">
                    <SectionLabel>Loading transactions…</SectionLabel>
                  </td>
                </tr>
              ) : filteredTx.length > 0 ? (
                filteredTx.map(t => (
                  <TxRow key={t.id ?? t.txId ?? t.createdAt} tx={t} />
                ))
              ) : (
                <tr>
                  <td
                    colSpan={7}
                    className="px-5 py-8 text-center text-xs text-nurev-muted"
                  >
                    {data != null
                      ? 'No transactions match this filter.'
                      : 'Backend endpoint /admin-services/exchange-routers returns ledger data once at least one router has API keys configured.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Spread comparison panel (conditional on API returning spreadByRouter) ── */}
      {summary.spreadByRouter && (
        <Card>
          <div className="mb-3 flex items-center gap-2">
            <TrendingDown size={14} className="text-amber-400" />
            <h3 className="text-sm font-bold text-nurev-text">
              Spread comparison (last 100 swaps)
            </h3>
          </div>
          <div className="space-y-2">
            {Object.entries(summary.spreadByRouter)
              .sort((a, b) => a[1] - b[1])
              .map(([router, spread], i) => (
                <div key={router} className="flex items-center gap-3">
                  <span className="w-24 truncate text-xs font-bold text-nurev-text">
                    {router}
                  </span>
                  <div className="flex-1 h-2 overflow-hidden rounded-full bg-white/[0.04]">
                    <div
                      className="h-full rounded-full transition-all"
                      style={{
                        width: `${Math.min(100, (spread / 0.04) * 100)}%`,
                        background:
                          i === 0
                            ? 'linear-gradient(90deg,#f59e0b,#fbbf24)'
                            : 'rgba(251,191,36,0.35)'
                      }}
                    />
                  </div>
                  <span className="w-16 text-right text-xs font-bold text-nurev-text">
                    {(spread * 100).toFixed(2)}%
                  </span>
                </div>
              ))}
          </div>
        </Card>
      )}
    </div>
  );
}

// ─── Transaction row sub-component ───────────────────────────────────────

function TxRow({ tx }: { tx: ExchangeTransaction }) {
  const spreadHigh =
    tx.spread != null && tx.spread > 0.02;

  return (
    <tr className="border-b border-nurev-border last:border-b-0 hover:bg-white/[0.02]">
      <td className="px-5 py-2.5 text-nurev-muted">
        {timeAgo(tx.createdAt)}
      </td>
      <td className="px-5 py-2.5 font-medium text-nurev-text">
        {tx.router}
      </td>
      <td className="px-5 py-2.5">
        <StatusBadge status={tx.status} />
      </td>
      <td className="px-5 py-2.5 font-mono text-[11px] text-nurev-text">
        {tx.fromAsset}
        <ArrowRight size={10} className="mx-1 inline text-nurev-muted" />
        {tx.toAsset}
      </td>
      <td className="px-5 py-2.5 text-right font-medium text-nurev-text">
        {Number(tx.amount).toFixed(4)}
      </td>
      <td className="px-5 py-2.5 text-right font-mono text-[11px] text-nurev-muted">
        {tx.rate != null ? Number(tx.rate).toFixed(6) : '—'}
      </td>
      <td className="px-5 py-2.5 text-right">
        {tx.spread != null ? (
          <span
            className={`font-bold ${
              spreadHigh ? 'text-red-400' : 'text-nurev-text'
            }`}
          >
            {(tx.spread * 100).toFixed(2)}%
          </span>
        ) : (
          '—'
        )}
      </td>
    </tr>
  );
}
